import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Select,
  Card,
  Badge,
  getStatusBadgeVariant,
  Pagination,
  CompactFilterBar,
  Modal,
  ModalFooter,
  Table,
} from '@/components/common';
import type { Column, SortDirection } from '@/components/common/Table';
// Imported by path rather than through components/common/index.ts, which is
// outside this task's file domain.
import { ContactAvatar, type ContactAvatarImage } from '@/components/common/ContactAvatar';
import { useToggleCustomerActive } from '@/hooks';
// Imported by path rather than through services/index.ts, which is outside this
// task's file domain -- the same reason ContactAvatar is imported by path above.
import api, { getErrorMessage } from '@/services/api';
import { useCan } from '@/hooks/useEmployeeRights';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useFilterParams } from '@/hooks/useFilterParams';
import { formatDate, kycStatusLabel, titleCase } from '@/utils';
import type { Customer, CustomerType, CustomerSegment, KycStatus } from '@/types';
import { useAuthStore } from '@/store/auth';
import { DuplicatesTab } from './DuplicatesTab';
import { ContactQuickActions } from './ContactQuickActions';
import { useCustomerList, type CustomerListFilters } from './customerListQuery';

/**
 * F2: `CustomerListSerializer` has carried `avatar`/`avatar_image` since the
 * 2026-07-30 media-library change; `types/index.ts` never gained them and is
 * outside this task's file domain, so the shape is declared here the same way
 * `InsurersListPage` declares its own image fields.
 */
type CustomerWithAvatar = Customer & {
  avatar?: string | null;
  avatar_image?: ContactAvatarImage | null;
};

/**
 * B4: what `POST /customers/bulk-delete/` answers.
 *
 * One entry per contact, never a count. "3 of 5 deleted" leaves the reader
 * hunting for which two, and a bulk action that half-works behind a bare 200 is
 * this codebase's most-repeated bug.
 */
type BulkDeleteOutcome = {
  id: string;
  name: string | null;
  status: 'deleted' | 'blocked' | 'not_found';
  message: string;
  blocked_by?: Record<string, number>;
};

type BulkDeleteResult = {
  requested: number;
  deleted_count: number;
  blocked_count: number;
  not_found_count: number;
  results: BulkDeleteOutcome[];
};

const customerTypeOptions = [
  { value: '', label: 'All Types' },
  { value: 'individual', label: 'Individual' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'huf', label: 'HUF' },
  { value: 'trust', label: 'Trust' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'llp', label: 'LLP' },
  { value: 'proprietorship', label: 'Proprietorship' },
];

const kycStatusOptions = [
  { value: '', label: 'All KYC Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'under_review', label: 'Under Review' },
  // "Completed" rather than "Verified": `completed` is the state
  // Customer.derive_kyc_status() actually sets when the required identity
  // fields are present, and the old option asked for `verified` — a manual
  // workflow state almost nothing is in — so filtering by it returned nearly
  // nothing however many contacts had finished their KYC. The server treats
  // this value as covering both.
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
  { value: 'reverify_required', label: 'Re-verification Required' },
];

const contactTypeOptions = [
  { value: '', label: 'All Contacts' },
  // #639: the type is derived, not chosen. Say what it means where it is picked.
  { value: 'client', label: 'Client (has an issued policy)' },
  { value: 'lead', label: 'Lead (no issued policy yet)' },
];

const segmentTypeOptions = [
  { value: '', label: 'All Segments' },
  { value: 'standard', label: 'Standard' },
  { value: 'premium', label: 'Premium' },
  { value: 'high_value', label: 'High Value' },
  { value: 'vip', label: 'VIP' },
  { value: 'corporate', label: 'Corporate' },
];

/**
 * Q-01 -- the filter state this page mirrors into the query string.
 *
 * Flat strings, because that is what `useFilterParams` can decode from a URL;
 * `queryFilters` below turns them back into the shape the API speaks
 * (`is_active` is a boolean on the wire, and an empty string means "send
 * nothing" rather than "send blank").
 *
 * DELIBERATELY NOT HERE: the page number. See `PAGE_IS_NOT_PERSISTED` below.
 * Nor the list/duplicates tab -- that is a different VIEW, not a filter of this
 * one, and putting it in the `f_` namespace would make Clear Filters throw the
 * reader out of the Duplicates review they were in the middle of.
 */
type ContactPanelFilters = {
  search: string;
  contact_type: string;
  is_active: string;
  customer_type: string;
  segment: string;
  kyc_status: string;
  /** U-04: the server ordering field, '' when the list is in its default order. */
  sort: string;
  sort_dir: string;
};

const EMPTY_FILTERS: ContactPanelFilters = {
  search: '',
  contact_type: '',
  is_active: '',
  customer_type: '',
  segment: '',
  kyc_status: '',
  sort: '',
  sort_dir: '',
};

/**
 * Q-01 note 3: this page reads an UNPREFIXED deep-link param (`?contact_type=`,
 * how the sidebar's "Leads" entry opens the lead slice), so its own filters
 * have to live in a separate namespace. Without this, picking "Client" from the
 * dropdown would write `?contact_type=client` -- the same param the mount-time
 * seed reads -- and the one-shot deep link would quietly become permanent.
 */
const FILTER_PREFIX = 'f_';

/**
 * Every UNPREFIXED param this page treats as a ONE-SHOT deep-link seed.
 *
 * `useFilterParams` owns only the `f_` namespace, so neither `clearAll()` nor a
 * filter the user empties can remove any of these. Left in place they outrank
 * the user on the NEXT mount, because `initial` below re-seeds from them and an
 * empty `f_` value is DELETED rather than written -- the namespace has no way
 * to say "deliberately empty". That is the pair of bugs the Policies pilot had
 * to fix (Clear Filters undone by a refresh; a deep-linked filter the user
 * cleared by hand coming back on reload), so the seed is CONSUMED here instead:
 * once the user has overridden the key it seeded, the param is deleted.
 */
const DEEP_LINK_PARAMS = ['contact_type'] as const;

/** Which unprefixed seed param(s) a given PANEL filter overrides. */
const SEED_PARAMS_FOR: Partial<Record<keyof ContactPanelFilters, readonly string[]>> = {
  contact_type: ['contact_type'],
};

/**
 * U-04: the fields this page offers as sort controls.
 *
 * `CustomerViewSet.ordering_fields` (backend/apps/customers/views.py:198) is
 * exactly `["created_at", "full_name", "total_premium_paid", "customer_since"]`.
 * Two of those are columns here; `created_at` and `total_premium_paid` are not
 * shown, so there is nothing to put a header on. Everything else on screen --
 * Contact Type, Customer Type, phone/email, KYC Status, Account Status -- is
 * NOT orderable server-side, and marking one of those `sortable` would render a
 * control that does nothing: DRF's OrderingFilter drops an unknown term
 * silently and returns the default order, so the click would "work" and the
 * list would come back exactly as it was. This project has shipped that bug
 * before. Adding a header for any of them needs a backend change first.
 */
const SORTABLE_FIELDS = new Set(['full_name', 'customer_since']);

/**
 * PAGE_IS_NOT_PERSISTED.
 *
 * The page number is deliberately kept out of the URL. Every filter change on
 * this page resets it to 1, and the debounced-search effect below re-asserts
 * that on mount -- so a page number restored from the query string would be
 * overwritten before the first paint, leaving a URL that says `page=3` over a
 * list showing page 1. The user lands on page 1 of their restored filters,
 * which is the honest outcome.
 */

export function CustomersListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  // Item 7. A Set, not an array: ticking and unticking is a membership
  // question and this keeps it one.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Q-01: the panel's own filters, mirrored into `?f_*` so they survive a
  // refresh, a bookmark and a shared link. Leads are merged into Contacts, so
  // a /contacts?contact_type=lead deep link still pre-selects the Lead filter
  // -- read ONCE here, in the hook's lazy initializer, and merged UNDER
  // anything already in the `f_` namespace, so a value the user actually chose
  // always beats the deep-link seed.
  const { filters, setFilter, setFilters, clearAll } = useFilterParams<ContactPanelFilters>({
    defaults: EMPTY_FILTERS,
    prefix: FILTER_PREFIX,
    initial: (params) => {
      const rawContactType = params.get('contact_type');
      return {
        contact_type:
          rawContactType === 'lead' || rawContactType === 'client' ? rawContactType : '',
      };
    },
  });

  // Task 9: search-as-you-type (global rule 4, already used on Policies).
  // `searchTerm` drives the input synchronously; `debouncedTerm` (300ms)
  // feeds the query so typing doesn't fire a request per keystroke. Seeded
  // from the restored `?f_search=` so a refresh brings the box back filled in,
  // not just the results.
  const { term: searchTerm, debouncedTerm, setTerm: setSearchTerm } = useDebouncedSearch(filters.search);

  // U-04: only a field the server can order by, so a stale/hand-edited
  // `?f_sort=` cannot leave a header drawn as sorted over a list that is not.
  const sortKey = SORTABLE_FIELDS.has(filters.sort) ? filters.sort : '';
  const sortDirection: SortDirection = filters.sort_dir === 'desc' ? 'desc' : 'asc';
  const ordering = sortKey ? `${sortDirection === 'desc' ? '-' : ''}${sortKey}` : undefined;

  // The wire shape. Memoized because it is the react-query key: rebuilding it
  // every render would refetch the list on every unrelated re-render.
  const queryFilters = useMemo<CustomerListFilters>(
    () => ({
      search: filters.search || undefined,
      contact_type: (filters.contact_type || undefined) as 'client' | 'lead' | undefined,
      customer_type: (filters.customer_type || undefined) as CustomerType | undefined,
      segment: (filters.segment || undefined) as CustomerSegment | undefined,
      kyc_status: (filters.kyc_status || undefined) as KycStatus | undefined,
      is_active: filters.is_active === '' ? undefined : filters.is_active === 'true',
      ordering,
    }),
    [filters, ordering]
  );

  const { data, isLoading, isError, refetch } = useCustomerList(page, queryFilters);
  const toggleActiveMutation = useToggleCustomerActive();
  const customers = useMemo(() => data?.results ?? [], [data]);
  // The one ticked contact, or null when nothing (or more than one) is ticked.
  // Every action reads this, so "exactly one" is decided in a single place
  // rather than re-tested at each button.
  const selectedContact =
    selectedIds.size === 1
      ? customers.find((c) => selectedIds.has(c.id)) ?? null
      : null;
  const totalItems = data?.count ?? 0;
  const itemsPerPage = 20;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  // B4: contacts Create / Read / Delete -- the cells `ModuleRight("contacts")`
  // checks on the API for these actions.
  const canAddCustomer = useCan('contacts', 'create');
  const canReadContacts = useCan('contacts', 'read');
  const canDeleteContactsRight = useCan('contacts', 'delete');
  // Duplicate review is broker-side only: the endpoints behind it read a whole
  // broker's contact book and move policies between records.
  const { user } = useAuthStore();
  const canReviewDuplicates =
    (user?.role === 'broker_admin' || user?.role === 'broker_employee') && canReadContacts;
  const [activeTab, setActiveTab] = useState<'list' | 'duplicates'>('list');

  // The same right the single delete answers to (`CustomerViewSet` gates
  // destroy and bulk_delete identically -- contacts Delete), plus the
  // broker-staff floor that destructive action has always had: a POSP can open
  // a contact but has never been able to delete one.
  const canDeleteContacts =
    canDeleteContactsRight &&
    (user?.role === 'super_admin' ||
      user?.role === 'country_admin' ||
      user?.role === 'broker_admin' ||
      user?.role === 'broker_employee');
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkDeleteResult | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { data } = await api.post<BulkDeleteResult>('/customers/bulk-delete/', { ids });
      return data;
    },
    onSuccess: (data) => {
      setBulkError(null);
      setBulkResult(data);
      // Only the ones that actually went are dropped from the selection, so a
      // second press retries nothing that already worked, and nothing that was
      // refused vanishes from view.
      const deleted = new Set(
        data.results.filter((row) => row.status === 'deleted').map((row) => row.id)
      );
      setSelectedIds((previous) => new Set([...previous].filter((id) => !deleted.has(id))));
      if (data.deleted_count > 0) {
        queryClient.invalidateQueries({ queryKey: ['customers'] });
      }
    },
    // Surfaced, not swallowed: without this the modal would sit there looking
    // like it had done something.
    onError: (error) => {
      setBulkResult(null);
      setBulkError(getErrorMessage(error));
    },
  });

  const closeBulkDelete = () => {
    setShowBulkDelete(false);
    setBulkResult(null);
    setBulkError(null);
  };

  // Push the debounced term into the filters and reset to the first page
  // whenever the effective search changes.
  //
  // Keyed off a ref of the LAST TERM PUSHED, not off `filters.search`. Seeded
  // with the restored value, it makes the mount pass a no-op (the box and the
  // filter already agree, so no second request goes out); and because Clear
  // resets `filters.search` while `debouncedTerm` still holds the old text for
  // another 300ms, comparing against `filters.search` here would immediately
  // re-apply the search the user just cleared.
  const pushedTermRef = useRef(filters.search);
  useEffect(() => {
    if (pushedTermRef.current === debouncedTerm) return;
    pushedTermRef.current = debouncedTerm;
    setFilter('search', debouncedTerm);
    setPage(1);
  }, [debouncedTerm, setFilter]);

  // A selection is about rows you can see. Paging or filtering replaces them, so
  // the bar would say "1 selected" while `selectedContact` resolved to nothing
  // on the new page and every action disappeared -- a claim about a state the
  // user could neither see nor act on.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, queryFilters]);

  /**
   * Delete the named UNPREFIXED deep-link params from the URL -- see
   * `DEEP_LINK_PARAMS`. Called whenever the user overrides a key one of them
   * seeded, so the seed cannot come back and outrank them on the next mount.
   *
   * Two writers in one tick is safe: `useFilterParams` defers ITS write to a
   * layout effect and issues it with the functional form against the query
   * string react-router has actually committed, precisely for this co-tenant
   * case.
   *
   * No-op when none of them is present, so an ordinary filter change on an
   * ordinary /contacts URL costs no extra navigation.
   */
  const consumeSeedParams = (names: readonly string[]) => {
    if (!names.some((name) => searchParams.has(name))) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const name of names) next.delete(name);
        return next;
      },
      { replace: true }
    );
  };

  const handleFilterChange = (key: keyof ContactPanelFilters, value: string) => {
    setFilters({ [key]: value } as Partial<ContactPanelFilters>);
    consumeSeedParams(SEED_PARAMS_FOR[key] ?? []);
    setPage(1);
  };

  // U-04: server-side sort. `Table` never reorders the rows it is given -- it
  // reports the next sort state and this sends it as DRF's `?ordering=`, so
  // the order is the whole filtered book's, not this page of twenty.
  const handleSortChange = (key: string, direction: SortDirection) => {
    setFilters({ sort: key, sort_dir: direction });
    setPage(1);
  };

  const handleRowClick = (customer: Customer) => {
    navigate(`/contacts/${customer.id}`);
  };

  // Task 9: count of the PANEL's own filters (search is always visible, so it
  // is excluded; the sort is a view preference, not a filter, so it does not
  // badge either) set to a non-default value. Badges the Filter toggle and
  // decides whether the panel starts expanded.
  const activeFilterCount = [
    filters.contact_type,
    filters.is_active,
    filters.customer_type,
    filters.segment,
    filters.kyc_status,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSearchTerm('');
    // Q-01: this is the one action that empties the `?f_*` namespace. It also
    // resets the sort, which lives in that namespace so a shared link
    // reproduces the view the sender was looking at.
    clearAll();
    // ...and this is what makes Clear survive a REFRESH. `clearAll` empties the
    // `f_` namespace only; the unprefixed deep-link param would otherwise sit
    // untouched in the URL and re-seed the filter the moment the page is
    // reloaded or the link bookmarked.
    consumeSeedParams(DEEP_LINK_PARAMS);
    setPage(1);
  };

  const toggleOne = (id: string) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * U-02: the columns, with a width each.
   *
   * SIZE = CONTENT + CELL PADDING. `Table` pads every `<td>` with `px-6`
   * (48px) and, under `layout="fixed"`, sets `truncate` -- i.e.
   * `overflow: hidden`, with cells `box-sizing: border-box`. A column declared
   * at 7rem therefore offers 64px of CONTENT, not 112px, and anything wider is
   * clipped with no scrollbar and no ellipsis. The tight columns below
   * override the padding to `px-3` (24px) through `className` -- `cn` is
   * tailwind-merge, so a `px-*` there beats the `px-6`:
   *
   *   select           3rem  px-3 ->  24px (a 16px checkbox + its focus ring)
   *   full_name       16rem  px-6 -> 208px (40px avatar + 12px mr-3 + 156px
   *                                         for the name over the code)
   *   contact_type     7rem  px-3 ->  88px ("Client" / "Lead" badge)
   *   customer_type    9rem  px-3 -> 120px ("Proprietorship" is the longest)
   *   contact         14rem  px-6 -> 176px (phone over email)
   *   kyc_status       9rem  px-3 -> 120px ("Re-verification Required" wraps to 2 lines)
   *   customer_since   8rem  px-3 -> 104px ("Jan 1, 2026": ~85px)
   *   account_status  10rem  px-3 -> 136px (badge ~62px + gap-3 12px + 36px
   *                                         toggle = 110px)
   *                  -------
   *                   76rem = 1216px, inside the ~1424px a 1440-1536px desktop
   *   can show without a horizontal page scrollbar. Narrower than that the
   *   TABLE -- not the page -- scrolls sideways inside its own card.
   *
   * Account Status holds an INTERACTIVE control (the activate toggle), so its
   * width is the one that must never be cut fine: a clipped control does not
   * ellipsise, it simply ceases to exist, exactly as the Policies pilot's Renew
   * button did.
   *
   * THE CELLS THAT RENDER JSX carry their own `title`. `Table` sets a tooltip
   * only when a cell renders to a string or a number (stringifying arbitrary
   * JSX would show "[object Object]"), so name, contact and the three badges
   * would otherwise ellipsise with nothing to hover. Each badge additionally
   * gets `max-w-full` with the label in a `truncate` span: an `inline-flex`
   * badge is an atomic inline box, which a `truncate` ancestor hard-CLIPS with
   * no ellipsis.
   */
  const columns: Column<Customer>[] = [
    {
      key: 'select',
      header: '',
      width: '3rem',
      className: 'px-3',
      render: (customer: Customer) => (
        // The row navigates on click, so the checkbox has to stop the event:
        // without this, ticking a contact is a trip to their detail page and
        // back, with the selection gone.
        <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            aria-label={customer.full_name}
            checked={selectedIds.has(customer.id)}
            onChange={() => toggleOne(customer.id)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 dark:text-primary-400 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
          />
        </span>
      ),
    },
    {
      key: 'full_name',
      header: 'Name',
      // #636: widths below sum to 68rem so every column, Account Status
      // included, fits the 1440px content area without a sideways scroll.
      width: '12rem',
      className: 'px-3',
      // U-04: `full_name` IS in CustomerViewSet.ordering_fields.
      sortable: true,
      render: (customer: Customer) => (
        <div className="flex items-center">
          <ContactAvatar
            contactId={customer.id}
            name={customer.full_name}
            image={(customer as CustomerWithAvatar).avatar_image}
            size="sm"
            className="mr-3 shrink-0"
          />
          <div className="min-w-0">
            <p
              className="truncate text-sm font-medium text-gray-900 dark:text-white"
              title={customer.full_name}
            >
              {customer.full_name}
            </p>
            <p className="truncate text-xs text-gray-500 dark:text-gray-400">
              {customer.customer_code}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'contact_type',
      header: 'Contact Type',
      width: '6rem',
      className: 'px-3',
      render: (customer: Customer) =>
        customer.contact_type ? (
          <Badge
            variant={customer.contact_type === 'client' ? 'success' : 'info'}
            size="sm"
            className="max-w-full"
          >
            <span
              className="truncate"
              title={
                customer.contact_type === 'client'
                  ? 'Client: has at least one issued policy'
                  : 'Lead: no issued policy yet'
              }
            >
              {titleCase(customer.contact_type)}
            </span>
          </Badge>
        ) : (
          // Was text-gray-400 on white: ~2.8:1, below the 4.5:1 floor for body
          // text. gray-500 carries the same "nothing here" weight and passes.
          <span className="text-xs text-gray-500 dark:text-gray-400">—</span>
        ),
    },
    {
      key: 'customer_type',
      header: 'Type',
      width: '6.5rem',
      className: 'px-3',
      // A plain string, so `Table` supplies the tooltip itself.
      render: (customer: Customer) => titleCase(customer.customer_type),
    },
    {
      key: 'contact',
      header: 'Contact',
      width: '11.5rem',
      className: 'px-3',
      render: (customer: Customer) => (
        <div>
          <p className="truncate text-sm text-gray-900 dark:text-white" title={customer.phone}>
            {customer.phone}
          </p>
          {customer.email && (
            <p className="truncate text-xs text-gray-500 dark:text-gray-400" title={customer.email}>
              {customer.email}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'kyc_status',
      header: 'KYC Status',
      width: '12.5rem',
      // dq-578: the badge also wraps onto two lines instead of being cut if the column is squeezed.
      className: 'px-3',
      render: (customer: Customer) => (
        <Badge variant={getStatusBadgeVariant(customer.kyc_status)} size="sm" className="max-w-full whitespace-normal text-center leading-tight">
          <span title={kycStatusLabel(customer.kyc_status)}>
            {kycStatusLabel(customer.kyc_status)}
          </span>
        </Badge>
      ),
    },
    {
      key: 'customer_since',
      header: 'Customer Since',
      width: '7rem',
      className: 'px-3',
      // U-04: `customer_since` IS in CustomerViewSet.ordering_fields.
      sortable: true,
      // A plain string, so `Table` supplies the tooltip itself.
      render: (customer: Customer) => formatDate(customer.customer_since),
    },
    {
      key: 'account_status',
      header: 'Account Status',
      width: '9.5rem',
      className: 'px-3',
      render: (customer: Customer) => (
        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
          <Badge variant={customer.is_active ? 'success' : 'default'} size="sm" className="max-w-full">
            <span className="truncate" title={customer.is_active ? 'Active' : 'Inactive'}>
              {customer.is_active ? 'Active' : 'Inactive'}
            </span>
          </Badge>
          <button
            type="button"
            role="switch"
            aria-checked={customer.is_active}
            aria-label={
              customer.is_active
                ? `Deactivate ${customer.full_name}`
                : `Activate ${customer.full_name}`
            }
            disabled={
              toggleActiveMutation.isPending && toggleActiveMutation.variables === customer.id
            }
            onClick={() => toggleActiveMutation.mutate(customer.id)}
            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed ${
              customer.is_active ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              aria-hidden="true"
              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-800 shadow ring-0 transition duration-200 ease-in-out ${
                customer.is_active ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Contacts"
        subtitle={`${totalItems} total contacts`}
        breadcrumbs={[{ label: 'Contacts' }]}
        actions={
          canAddCustomer ? (
            <Link to="/contacts/new">
              <Button leftIcon={<PlusIcon className="w-5 h-5" />}>Add Contact</Button>
            </Link>
          ) : undefined
        }
      />

      {canReviewDuplicates && (
        <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex gap-6" aria-label="Contacts views">
            {([
              { id: 'list' as const, label: 'All Contacts' },
              { id: 'duplicates' as const, label: 'Duplicates' },
            ]).map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                aria-current={activeTab === tab.id ? 'page' : undefined}
                className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {activeTab === 'duplicates' ? (
        <DuplicatesTab />
      ) : (
      <>
      {/* Task 9 (Broker 2): one line by default -- search box + Filter toggle
          badged with the active count. The five filters below used to sit on
          their own row (three of them behind a now-removed disclosure that
          could leave a filter applied while invisible); they now live in the
          panel CompactFilterBar reveals on expand, and the panel starts
          expanded whenever one of them is already set — including one restored
          from `?f_*` after a refresh. */}
      <CompactFilterBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Name, email, phone, or code..."
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
        columns={5}
      >
        <Select
          id="contact-filter-contact-type"
          label="Contact Type"
          options={contactTypeOptions}
          value={filters.contact_type}
          onChange={(e) => handleFilterChange('contact_type', e.target.value)}
        />
        <Select
          id="contact-filter-status"
          label="Status"
          options={[
            { value: '', label: 'All Status' },
            { value: 'true', label: 'Active' },
            { value: 'false', label: 'Inactive' },
          ]}
          value={filters.is_active}
          onChange={(e) => handleFilterChange('is_active', e.target.value)}
        />
        <Select
          id="contact-filter-customer-type"
          label="Customer Type"
          options={customerTypeOptions}
          value={filters.customer_type}
          onChange={(e) => handleFilterChange('customer_type', e.target.value)}
        />
        <Select
          id="contact-filter-segment"
          label="Segment"
          options={segmentTypeOptions}
          value={filters.segment}
          onChange={(e) => handleFilterChange('segment', e.target.value)}
        />
        <Select
          id="contact-filter-kyc-status"
          label="KYC Status"
          options={kycStatusOptions}
          value={filters.kyc_status}
          onChange={(e) => handleFilterChange('kyc_status', e.target.value)}
        />
      </CompactFilterBar>

      {/* Item 7: what you can do to the ticked contact. All four actions are
          single-contact actions -- a quote, a policy or a claim belongs to one
          person -- so with two or more ticked there is no honest answer to
          "which contact is this for" and the bar shows the count alone. Acting
          on the first ticked row was considered and rejected: it always works,
          and it is silently wrong whenever the reader meant the other one. */}
      {selectedIds.size > 0 && (
        <Card className="mb-4">
          <div
            role="region"
            aria-label="Selected contacts"
            className="flex flex-wrap items-center justify-between gap-3"
          >
            <div className="text-sm text-gray-700 dark:text-gray-200">
              <span className="font-medium">{selectedIds.size} selected</span>
              {selectedContact && (
                <span className="text-gray-500 dark:text-gray-400"> · {selectedContact.full_name}</span>
              )}
              {selectedIds.size > 1 && (
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  A quote, a policy or a claim belongs to one person — leave just one selected to
                  use those. Delete works on the whole selection.
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {selectedContact && (
                <ContactQuickActions
                  contactId={selectedContact.id}
                  contactName={selectedContact.full_name}
                  size="sm"
                  // A bare #fragment does nothing here: nothing in the app
                  // scrolls to it, so the reader landed at the top of the
                  // contact page with the records off screen. `?tab=records` is
                  // read by CustomerDetailPage, which scrolls once the records
                  // section has mounted.
                  onViewPolicies={() =>
                    navigate(`/contacts/${selectedContact.id}?tab=records`)
                  }
                />
              )}
              {/* B4: the checkboxes and this bar have been here since item 7;
                  the one thing a multi-selection is genuinely FOR was the thing
                  missing. */}
              {canDeleteContacts && (
                <Button
                  variant="danger"
                  size="sm"
                  leftIcon={<TrashIcon className="w-4 h-4" />}
                  onClick={() => {
                    setBulkResult(null);
                    setBulkError(null);
                    setShowBulkDelete(true);
                  }}
                >
                  {selectedIds.size === 1 ? 'Delete' : `Delete ${selectedIds.size}`}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())}>
                Clear selection
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* A REFRESH that failed while the rows it was replacing are still in
          hand -- a refetch of the same query, which react-query leaves showing
          its previous data. That is what happens after a bulk delete
          invalidates the list and the reload then fails: the modal says two
          contacts went, the rows on screen are the ones from before, and
          nothing says the two disagree. (A filter CHANGE is a different query
          key, and a failure there drops to the full error branch below.) */}
      {isError && customers.length > 0 && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300"
        >
          <span>Could not refresh this list. These are the results from before your last change.</span>
          <button type="button" className="font-medium underline" onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      )}

      {/* Contacts table.
          Three states, never two: a failed request, an empty book and a
          loading list are different facts and each has its own branch. */}
      {isLoading ? (
        // The shared Table's own skeleton, which replaced a hand-rolled one
        // that had no dark-mode classes at all.
        <Table<Customer> data={[]} columns={columns} isLoading rowKey={(c) => c.id} />
      ) : isError && customers.length === 0 ? (
        // A failed list request is NOT an empty contact book. Rendering the
        // empty state here told a broker "no contacts found" — and offered
        // "add your first contact" — when the request had simply failed.
        <Card padding="none">
          <div role="alert" className="px-6 py-12 text-center">
            <p className="text-sm font-medium text-red-700 dark:text-red-400">
              Could not load contacts.
            </p>
            <button
              type="button"
              className="mt-3 text-sm text-primary-600 underline hover:no-underline dark:text-primary-400"
              onClick={() => void refetch()}
            >
              Try again
            </button>
          </div>
        </Card>
      ) : customers.length === 0 ? (
        <Card padding="none">
          <EmptyState />
        </Card>
      ) : (
        <>
          <Table<Customer>
            data={customers}
            columns={columns}
            rowKey={(customer) => customer.id}
            onRowClick={handleRowClick}
            // U-02: fixed widths (no column jump when the filters change) and
            // a header that stays put while the rows scroll under it.
            layout="fixed"
            stickyHeader
            // U-04: the Table never reorders anything -- it reports the next
            // sort state and the server does the ordering.
            sortKey={sortKey || undefined}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
          />
          {totalPages > 1 && (
            <Card padding="none" className="mt-4 overflow-hidden">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            </Card>
          )}
        </>
      )}
      </>
      )}

      {/* B4: confirm, then say what happened to EACH contact.
          Deleting a contact is a soft delete an administrator can restore, and
          one holding policies, quotes or claims is refused outright -- so both
          halves of the truth are on screen before and after the press. */}
      <Modal
        isOpen={showBulkDelete}
        onClose={closeBulkDelete}
        title={bulkResult ? 'What happened' : `Delete ${selectedIds.size} contact${selectedIds.size === 1 ? '' : 's'}?`}
        description={
          bulkResult
            ? undefined
            : 'Each one is hidden from Contacts and can be restored by an administrator. Any contact that still has policies, quotes or claims is refused and left alone.'
        }
      >
        {bulkError && (
          <div
            className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-900/20"
            data-testid="bulk-delete-error"
          >
            <p className="text-sm font-medium text-red-800 dark:text-red-300">Nothing was deleted.</p>
            <p className="mt-1 text-xs text-red-700 dark:text-red-400">{bulkError}</p>
          </div>
        )}

        {bulkResult && (
          <div className="space-y-3" data-testid="bulk-delete-result">
            <p className="text-sm text-gray-700 dark:text-gray-200">
              {bulkResult.deleted_count} deleted
              {bulkResult.blocked_count > 0 && `, ${bulkResult.blocked_count} kept`}
              {bulkResult.not_found_count > 0 && `, ${bulkResult.not_found_count} not found`}.
            </p>
            <ul className="space-y-2">
              {bulkResult.results.map((row) => (
                <li
                  key={row.id}
                  data-testid={`bulk-delete-row-${row.status}`}
                  className={
                    row.status === 'deleted'
                      ? 'rounded border border-gray-200 p-2 text-xs text-gray-600 dark:border-gray-700 dark:text-gray-300'
                      : 'rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300'
                  }
                >
                  <span className="font-medium">{row.name ?? 'This contact'}</span> — {row.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        <ModalFooter>
          <Button variant="outline" onClick={closeBulkDelete}>
            {bulkResult ? 'Close' : 'Cancel'}
          </Button>
          {!bulkResult && (
            <Button
              variant="danger"
              isLoading={bulkDeleteMutation.isPending}
              onClick={() => bulkDeleteMutation.mutate([...selectedIds])}
            >
              Delete
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
}

function EmptyState() {
  const canAddCustomer = useCan('contacts', 'create');
  return (
    <div className="text-center py-12">
      <div className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">No contacts found</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Get started by adding your first contact.
      </p>
      {canAddCustomer && (
        <div className="mt-6">
          <Link to="/contacts/new">
            <Button leftIcon={<PlusIcon className="w-5 h-5" />}>Add Contact</Button>
          </Link>
        </div>
      )}
    </div>
  );
}
